<template>
  <!-- Middle step of the "Create & Share a Board" guided flow: shows the few
  ads we're about to drop into the freshly created board and asks for a
  confirm, so the share step has something worth sharing. -->
  <BaseModal
    card-width="520px"
    no-padding
    no-toolbar
    no-exit
    @close="onClose"
  >
    <div
      data-tour="board-seed"
      class="flex flex-col px-6 pt-4 pb-6"
    >
      <!-- Post-save handoff: keep the modal up while saves settle and the
      board page mounts, so the share step doesn't race an empty board -->
      <div
        v-if="handingOff"
        class="flex flex-col items-center justify-center py-16 gap-3"
      >
        <BaseLoadingSpinnerCircle
          :width="20"
          :height="20"
          class="text-icon-normal"
        />
        <BaseText
          type="body"
          size="sm"
          class="text-text-muted"
        >
          Adding ads to your board…
        </BaseText>
      </div>

      <template v-else>
        <!-- Header: kicker + close -->
        <div class="flex items-center justify-between gap-3">
          <div class="flex items-center gap-1.5 text-text-subdued">
            <MouseIcon
              :width="14"
              :height="14"
            />
            <BaseText
              type="heading"
              size="overline"
              class="uppercase text-text-subdued"
            >
              {{ copy.kicker }}
            </BaseText>
          </div>
          <button
            type="button"
            aria-label="Close"
            class="seed-close -mr-1.5 w-7 h-7 rounded-md flex items-center justify-center text-icon-normal hover:bg-neutral-50 hover:text-icon-muted focus:outline-none"
            @click="$emit('close')"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="12"
              height="12"
              viewBox="0 0 16 16"
              fill="none"
            >
              <path
                d="M3.5 3.5L12.5 12.5M12.5 3.5L3.5 12.5"
                stroke="currentColor"
                stroke-width="1.5"
                stroke-linecap="round"
              />
            </svg>
          </button>
        </div>

        <BaseText
          type="display"
          size="xs"
          class="text-text-loud mt-3"
        >
          {{ copy.title }}
        </BaseText>
        <BaseText
          type="body"
          size="sm"
          class="text-text-muted mt-1.5"
        >
          {{ blurb }}
        </BaseText>

        <!-- Ad previews -->
        <div class="mt-5 grid grid-cols-5 gap-2">
          <template v-if="loading">
            <div
              v-for="i in MAX_ADS"
              :key="`seed-skeleton-${i}`"
              class="skeleton rounded-lg"
              style="aspect-ratio: 4 / 5"
            />
          </template>
          <template v-else>
            <div
              v-for="ad in ads"
              :key="ad.id"
              class="seed-thumb relative rounded-lg overflow-hidden bg-neutral-50 border border-border-normal"
              style="aspect-ratio: 4 / 5"
            >
              <img
                v-if="thumbnailFor(ad)"
                :src="thumbnailFor(ad)"
                alt=""
                class="w-full h-full object-cover"
              >
              <div
                v-else
                class="w-full h-full flex items-center justify-center"
              >
                <BaseText
                  type="label"
                  size="xs"
                  class="text-text-subdued"
                >
                  Ad
                </BaseText>
              </div>
              <div class="absolute bottom-1 left-1 right-1 flex items-center gap-1 px-1 py-0.5 rounded bg-neutral-900 bg-opacity-70">
                <img
                  v-if="ad.avatar"
                  :src="ad.avatar"
                  alt=""
                  class="w-3 h-3 rounded-sm flex-shrink-0"
                >
                <BaseText
                  type="label"
                  size="xs"
                  class="text-white truncate"
                >
                  {{ ad.name || ad.brand_name || 'Ad' }}
                </BaseText>
              </div>
            </div>
          </template>
        </div>

        <!-- Empty fallback: nothing to seed — let them continue anyway -->
        <div
          v-if="!loading && !ads.length"
          class="mt-3 flex items-center gap-2 p-2 rounded-md bg-neutral-25"
        >
          <BaseText
            type="body"
            size="sm"
            class="text-text-muted"
          >
            {{ copy.emptyNote }}
          </BaseText>
        </div>

        <div class="flex items-center justify-between gap-3 mt-6">
          <BaseText
            type="body"
            size="xs"
            class="text-text-subdued"
          >
            {{ boardLabel }}
          </BaseText>
          <BaseButton
            primary
            :disabled="loading || saving"
            :loading="saving"
            @click="confirm"
          >
            {{ ads.length ? copy.ctaLabel : copy.ctaLabelEmpty }}
          </BaseButton>
        </div>
      </template>
    </div>
  </BaseModal>
</template>

<script>
import posthog from 'posthog-js'
import ForeplayAPI from '@/api/foreplayServer'
import { SETUP_BOARD_SEED } from '@/utils/setup/setupContent'
import MouseIcon from '../globals/Icons/MouseIcon.vue'
const MAX_ADS = 5
// Saves aren't queryable the moment they resolve, so we poll the board until
// the ads come back before handing off — otherwise the board page mounts empty.
const POLL_INTERVAL_MS = 700
const POLL_TIMEOUT_MS = 9000
const SEED_WARNING = "Some ads couldn't be added. The board is still ready to share."

export default {
  name: 'SetupBoardSeedModal',
  components: {
    MouseIcon
  },
  props: {
    boardId: {
      type: String,
      required: true
    },
    boardName: {
      type: String,
      default: ''
    },
    // { brandId, name } of the brand tracked in the Spyder flow, if any
    brand: {
      type: Object,
      default: null
    }
  },
  data () {
    return {
      copy: SETUP_BOARD_SEED,
      MAX_ADS,
      ads: [],
      loading: true,
      saving: false,
      handingOff: false
    }
  },
  computed: {
    blurb () {
      const source = this.brand?.name || 'Discovery'
      return this.copy.blurb.replace('{source}', source)
    },
    boardLabel () {
      if (!this.boardName) return ''
      const name = this.boardName.startsWith('#') ? this.boardName : `#${this.boardName}`
      return `Adding to ${name}`
    }
  },
  created () {
    this.fetchAds()
  },
  beforeDestroy () {
    this.torn = true
    clearTimeout(this.pollTimer)
  },
  methods: {
    async fetchAds () {
      this.loading = true
      try {
        let results = []
        if (this.brand?.brandId) {
          const response = await ForeplayAPI.Ads.getSpyderAds(null, { orBrands: [this.brand.brandId] })
          results = response?.results || []
        }
        if (!results.length) {
          const response = await ForeplayAPI.Ads.getDiscoveryAds(null, {})
          results = response?.results || []
        }
        this.ads = results.slice(0, MAX_ADS)
      } catch (error) {
        console.error('Error loading ads to seed the board', error)
        this.ads = []
      } finally {
        this.loading = false
      }
    },
    onClose () {
      if (this.handingOff) return
      this.$emit('close')
    },
    thumbnailFor (ad) {
      if (!ad) return null
      const card = ad.cards?.[0]
      return ad.thumbnail || ad.image || card?.thumbnail || card?.image || null
    },
    delay (ms) {
      return new Promise((resolve) => {
        this.pollTimer = setTimeout(resolve, ms)
      })
    },
    // Resolves true once the board returns at least `expectedCount` ads,
    // false if we run out of patience.
    async waitForBoardAds (expectedCount) {
      if (!expectedCount) return true
      const deadline = Date.now() + POLL_TIMEOUT_MS
      while (!this.torn && Date.now() < deadline) {
        try {
          const { results } = await ForeplayAPI.Ads.getBoardAds(this.boardId, null, {})
          if ((results?.length || 0) >= expectedCount) return true
        } catch (error) {
          console.error('Error checking the seeded board', error)
        }
        await this.delay(POLL_INTERVAL_MS)
      }
      return false
    },
    async confirm () {
      if (this.saving || this.handingOff) return
      this.saving = true
      let payload = { adCount: 0, adThumbnails: [] }
      let savedCount = 0
      try {
        await Promise.all(this.ads.map((ad) => {
          const savePayload = { ...ad, board_ids: [this.boardId], createdAt: +new Date() }
          delete savePayload.relatedAds
          delete savePayload.sameCreativeCount
          if (savePayload.cards) {
            savePayload.cards = savePayload.cards.map((card) => {
              const cleaned = { ...card }
              delete cleaned.relatedAds
              delete cleaned.sameCreativeCount
              return cleaned
            })
          }
          return ForeplayAPI.Ads.save(savePayload)
        }))
        posthog.capture('Setup Board Seeded', { board_id: this.boardId, ad_count: this.ads.length })
        savedCount = this.ads.length
        payload = {
          adCount: this.ads.length,
          adThumbnails: this.ads.map(this.thumbnailFor).filter(Boolean)
        }
      } catch (error) {
        // Seeding is best-effort — the board just starts emptier
        console.error('Error seeding board ads', error)
        this.$showAlert({ type: 'warning', message: SEED_WARNING })
      }
      this.saving = false
      this.handingOff = true
      const landed = await this.waitForBoardAds(savedCount)
      if (this.torn) return
      if (!landed) this.$showAlert({ type: 'warning', message: SEED_WARNING })
      this.$emit('seeded', payload)
    }
  }
}
</script>

<style scoped>
.seed-close {
  transition: background-color 150ms ease, color 150ms ease;
}
.seed-thumb {
  animation: seed-thumb-in 260ms cubic-bezier(0.2, 0.8, 0.2, 1) both;
}
.seed-thumb:nth-child(2) { animation-delay: 40ms; }
.seed-thumb:nth-child(3) { animation-delay: 80ms; }
.seed-thumb:nth-child(4) { animation-delay: 120ms; }
.seed-thumb:nth-child(5) { animation-delay: 160ms; }
@keyframes seed-thumb-in {
  from {
    opacity: 0;
    transform: translateY(6px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
